// ─────────────────────────────────────────────────────────────
// Transit Duration Service
// Calculates when a transit entered orb, when it peaks, and
// when it exits, plus accumulated impact for long-term transits.
// ─────────────────────────────────────────────────────────────

import type { Planet } from './ephemeris';
import { PLANET_MEAN_SPEED } from './ephemeris';

// ─── Types ──────────────────────────────────────────────────

export type TransitPhase =
  | 'approaching'  // entering orb, growing effect
  | 'peak'         // exact or orb < 1°
  | 'separating'   // leaving orb, fading effect
  | 'long_term';   // active 30+ days, background life influence

export type TransitDuration =
  | 'days'    // < 7 total days
  | 'weeks'   // 7–29 days
  | 'months'  // 30–179 days
  | 'years';  // 180+ days

export interface TransitWindow {
  startDate: Date;
  exactDate: Date;
  endDate: Date;
  daysActive: number;
  daysRemaining: number;
  totalWindowDays: number;
  phase: TransitPhase;
  durationType: TransitDuration;
  isLongTerm: boolean; // daysActive >= 30
  isRetrograde: boolean;
  phaseNarrativePt: string;
  durationSummaryPt: string;
}

// ─── Core calculation ───────────────────────────────────────

export function calculateTransitWindow(
  transitPlanet: Planet,
  natalLongitude: number,
  aspectAngle: number,
  currentDate: Date,
  currentTransitLongitude: number,
  isRetrograde: boolean,
  maxOrb: number,
): TransitWindow {
  // Target longitude where the aspect is exact
  const targetLon = ((natalLongitude + aspectAngle) % 360 + 360) % 360;

  // Speed for duration estimates
  const speed = Math.abs(PLANET_MEAN_SPEED[transitPlanet]) || 0.01;
  const orbInDays = maxOrb / speed;

  // Angular distance to exact
  let diff = currentTransitLongitude - targetLon;
  if (diff > 180) diff -= 360;
  if (diff < -180) diff += 360;
  const normalizedDiff = Math.abs(diff);

  // Days to exact (positive = future, negative = past)
  const daysToExact = (normalizedDiff / speed) * (diff > 0 && !isRetrograde ? -1 : 1);
  const exactDate = new Date(currentDate.getTime() + daysToExact * 86_400_000);

  const startDate = new Date(exactDate.getTime() - orbInDays * 86_400_000);
  const endDate = new Date(exactDate.getTime() + orbInDays * 86_400_000);

  const daysActive = Math.max(0, Math.round((currentDate.getTime() - startDate.getTime()) / 86_400_000));
  const daysRemaining = Math.max(0, Math.round((endDate.getTime() - currentDate.getTime()) / 86_400_000));
  const totalWindowDays = Math.round((endDate.getTime() - startDate.getTime()) / 86_400_000);

  const isLongTerm = daysActive >= 30;

  // Determine phase
  let phase: TransitPhase;
  if (normalizedDiff < 1) {
    phase = 'peak';
  } else if (isLongTerm) {
    phase = 'long_term';
  } else if (daysToExact > 0) {
    phase = 'approaching';
  } else {
    phase = 'separating';
  }

  // Duration type
  let durationType: TransitDuration;
  if (totalWindowDays < 7) durationType = 'days';
  else if (totalWindowDays < 30) durationType = 'weeks';
  else if (totalWindowDays < 180) durationType = 'months';
  else durationType = 'years';

  // Portuguese narratives
  const phaseMap: Record<TransitPhase, string> = {
    approaching: `Iniciando, os efeitos estão crescendo. Ainda ${daysRemaining} dias de intensidade.`,
    peak: 'No pico máximo agora, influência no seu ponto mais forte.',
    separating: `Concluindo, a energia está se dissipando. Mais ${daysRemaining} dias para encerrar.`,
    long_term: `Configuração de fundo, ativa há ${daysActive} dias, integrando-se ao cotidiano.`,
  };

  const durationSummaryPt = isLongTerm
    ? `Ativo há ${daysActive} dias · Ainda ${daysRemaining} dias`
    : daysActive === 0
      ? `Iniciando hoje · Duração prevista: ${totalWindowDays} dias`
      : `Ativo há ${daysActive} dias · Encerra em ${daysRemaining} dias`;

  return {
    startDate,
    exactDate,
    endDate,
    daysActive,
    daysRemaining,
    totalWindowDays,
    phase,
    durationType,
    isLongTerm,
    isRetrograde,
    phaseNarrativePt: phaseMap[phase],
    durationSummaryPt,
  };
}
